/**
 * Consumer locale SSOT for the marketing site.
 * Must match RRAIMVP3 `src/shared/i18n/supported-locales.ts` (42 codes).
 * Do not strip `pt-BR` / `zh-Hans` / `zh-Hant`.
 */

export const SUPPORTED_LOCALES = [
  "en",
  "es",
  "fr",
  "de",
  "it",
  "pt",
  "pt-BR",
  "nl",
  "pl",
  "sv",
  "da",
  "no",
  "fi",
  "cs",
  "sk",
  "sl",
  "hr",
  "hu",
  "ro",
  "el",
  "ca",
  "ru",
  "uk",
  "tr",
  "ar",
  "he",
  "ur",
  "hi",
  "bn",
  "gu",
  "mr",
  "ta",
  "te",
  "pa",
  "ja",
  "ko",
  "zh-Hans",
  "zh-Hant",
  "id",
  "ms",
  "vi",
  "th",
] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "en";

export const RTL_LOCALES = ["ar", "he", "ur"] as const;

export type RtlLocale = (typeof RTL_LOCALES)[number];

export const LOCALE_STORAGE_KEY = "rr_locale";

const SUPPORTED_SET = new Set<string>(SUPPORTED_LOCALES);

/** English / native labels for the language picker. */
export const LOCALE_DISPLAY_NAMES: Record<
  Locale,
  { name: string; nativeName: string; code: string }
> = {
  en: { name: "English", nativeName: "English", code: "EN" },
  es: { name: "Spanish", nativeName: "Español", code: "ES" },
  fr: { name: "French", nativeName: "Français", code: "FR" },
  de: { name: "German", nativeName: "Deutsch", code: "DE" },
  it: { name: "Italian", nativeName: "Italiano", code: "IT" },
  pt: { name: "Portuguese", nativeName: "Português", code: "PT" },
  "pt-BR": {
    name: "Portuguese (Brazil)",
    nativeName: "Português (Brasil)",
    code: "PT-BR",
  },
  nl: { name: "Dutch", nativeName: "Nederlands", code: "NL" },
  pl: { name: "Polish", nativeName: "Polski", code: "PL" },
  sv: { name: "Swedish", nativeName: "Svenska", code: "SV" },
  da: { name: "Danish", nativeName: "Dansk", code: "DA" },
  no: { name: "Norwegian", nativeName: "Norsk", code: "NO" },
  fi: { name: "Finnish", nativeName: "Suomi", code: "FI" },
  cs: { name: "Czech", nativeName: "Čeština", code: "CS" },
  sk: { name: "Slovak", nativeName: "Slovenčina", code: "SK" },
  sl: { name: "Slovenian", nativeName: "Slovenščina", code: "SL" },
  hr: { name: "Croatian", nativeName: "Hrvatski", code: "HR" },
  hu: { name: "Hungarian", nativeName: "Magyar", code: "HU" },
  ro: { name: "Romanian", nativeName: "Română", code: "RO" },
  el: { name: "Greek", nativeName: "Ελληνικά", code: "EL" },
  ca: { name: "Catalan", nativeName: "Català", code: "CA" },
  ru: { name: "Russian", nativeName: "Русский", code: "RU" },
  uk: { name: "Ukrainian", nativeName: "Українська", code: "UK" },
  tr: { name: "Turkish", nativeName: "Türkçe", code: "TR" },
  ar: { name: "Arabic", nativeName: "العربية", code: "AR" },
  he: { name: "Hebrew", nativeName: "עברית", code: "HE" },
  ur: { name: "Urdu", nativeName: "اردو", code: "UR" },
  hi: { name: "Hindi", nativeName: "हिन्दी", code: "HI" },
  bn: { name: "Bengali", nativeName: "বাংলা", code: "BN" },
  gu: { name: "Gujarati", nativeName: "ગુજરાતી", code: "GU" },
  mr: { name: "Marathi", nativeName: "मराठी", code: "MR" },
  ta: { name: "Tamil", nativeName: "தமிழ்", code: "TA" },
  te: { name: "Telugu", nativeName: "తెలుగు", code: "TE" },
  pa: { name: "Punjabi", nativeName: "ਪੰਜਾਬੀ", code: "PA" },
  ja: { name: "Japanese", nativeName: "日本語", code: "JA" },
  ko: { name: "Korean", nativeName: "한국어", code: "KO" },
  "zh-Hans": { name: "Chinese (Simplified)", nativeName: "简体中文", code: "ZH" },
  "zh-Hant": {
    name: "Chinese (Traditional)",
    nativeName: "繁體中文",
    code: "ZH-HANT",
  },
  id: { name: "Indonesian", nativeName: "Bahasa Indonesia", code: "ID" },
  ms: { name: "Malay", nativeName: "Bahasa Melayu", code: "MS" },
  vi: { name: "Vietnamese", nativeName: "Tiếng Việt", code: "VI" },
  th: { name: "Thai", nativeName: "ไทย", code: "TH" },
};

/**
 * Store / browser tags that are not themselves UI codes.
 * `en-US` → `en` is handled by primary-tag fallback, not this table.
 */
const LOCALE_ALIASES: Record<string, Locale> = {
  "zh-cn": "zh-Hans",
  "zh-sg": "zh-Hans",
  "zh-hans": "zh-Hans",
  "zh-tw": "zh-Hant",
  "zh-hk": "zh-Hant",
  "zh-mo": "zh-Hant",
  "zh-hant": "zh-Hant",
  "pt-br": "pt-BR",
  iw: "he",
  "iw-il": "he",
  "he-il": "he",
  nb: "no",
  nn: "no",
  "nb-no": "no",
  "nn-no": "no",
  "no-no": "no",
  in: "id",
  "id-id": "id",
};

function canonicalFromSupported(tag: string): Locale | null {
  for (const loc of SUPPORTED_LOCALES) {
    if (loc.toLowerCase() === tag) return loc;
  }
  return null;
}

export function isLocale(value: string | null | undefined): value is Locale {
  return typeof value === "string" && SUPPORTED_SET.has(value);
}

export function isRtlLocale(locale: string | null | undefined): boolean {
  const normalized = normalizeLocaleOrNull(locale);
  return normalized === "ar" || normalized === "he" || normalized === "ur";
}

/**
 * Map any BCP-47 / underscore tag onto a consumer UI locale.
 * Preserves `pt-BR`, `zh-Hans`, and `zh-Hant` (does not strip those subtags).
 */
export function normalizeLocaleOrNull(
  value: string | null | undefined,
): Locale | null {
  if (!value) return null;
  const lower = value.toLowerCase().trim().replace(/_/g, "-");
  if (!lower) return null;

  const exact = canonicalFromSupported(lower);
  if (exact) return exact;
  if (LOCALE_ALIASES[lower]) return LOCALE_ALIASES[lower];

  const parts = lower.split("-").filter(Boolean);
  if (parts.length === 0) return null;

  if (parts.length >= 2) {
    const two = `${parts[0]}-${parts[1]}`;
    const twoExact = canonicalFromSupported(two);
    if (twoExact) return twoExact;
    if (LOCALE_ALIASES[two]) return LOCALE_ALIASES[two];
  }

  if (parts[0] === "zh") return "zh-Hans";

  const primary = canonicalFromSupported(parts[0]);
  if (primary) return primary;
  if (LOCALE_ALIASES[parts[0]]) return LOCALE_ALIASES[parts[0]];

  return null;
}

/** Always returns a supported locale (unknown → English). */
export function normalizeLocale(value: string | null | undefined): Locale {
  return normalizeLocaleOrNull(value) ?? DEFAULT_LOCALE;
}

export function filterLocales(query: string): Locale[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...SUPPORTED_LOCALES];
  return SUPPORTED_LOCALES.filter((code) => {
    const display = LOCALE_DISPLAY_NAMES[code];
    return (
      code.toLowerCase().includes(q) ||
      display.code.toLowerCase().includes(q) ||
      display.name.toLowerCase().includes(q) ||
      display.nativeName.toLowerCase().includes(q)
    );
  });
}
